import { sportCategories, newsArticles } from '@/mocks/homeData';

interface BreadcrumbItem {
  name: string;
  url: string;
}

function buildBreadcrumb(items: BreadcrumbItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `https://www.jiuzhousports.com${item.url}`,
    })),
  };
}

export function OrganizationSchema() {
  const json = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '九州体育',
    url: 'https://www.jiuzhousports.com',
    description: '九州体育是专业的综合体育服务平台，涵盖体育赛事直播、体育资讯、体育竞猜及运动健康等全场景服务。',
    email: 'service@jiuzhousports.com',
    telephone: '+86-400-888-9999',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '北辰西路8号北辰世纪中心A座15层',
      addressLocality: '北京市',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
    sameAs: [
      'https://weibo.com/jiuzhousports',
      'https://www.jiuzhousports.com',
    ],
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(json) }} />;
}

export function WebsiteSchema() {
  const json = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '九州体育',
    url: 'https://www.jiuzhousports.com',
    description: '九州体育是专业的综合体育服务平台，涵盖体育赛事直播、体育资讯、体育竞猜及运动健康等全场景服务。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.jiuzhousports.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(json) }} />;
}

export function HomePageSchema() {
  const json = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '九州体育官网 - 全场景体育服务平台',
    description: '九州体育是专业的综合体育服务平台，涵盖体育赛事直播、体育资讯、体育竞猜及运动健康等全场景服务。',
    url: 'https://www.jiuzhousports.com/',
    isPartOf: {
      '@type': 'WebSite',
      name: '九州体育',
      url: 'https://www.jiuzhousports.com',
    },
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(json) }} />;
}

export function BreadcrumbSchema({ items }: { items: BreadcrumbItem[] }) {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(buildBreadcrumb(items)) }} />;
}

export function ArticleSchema({ articleId }: { articleId: number }) {
  const article = newsArticles.find((a) => a.id === articleId);
  if (!article) return null;
  const catLabels: Record<string, string> = {
    football: '足球', basketball: '篮球', esports: '电竞', racing: '赛车', tennis: '网球', swimming: '游泳',
  };
  const json = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    url: `https://www.jiuzhousports.com/article/${article.id}`,
    articleSection: catLabels[article.category] || article.category,
    keywords: article.tags.join(', '),
    publisher: {
      '@type': 'Organization',
      name: '九州体育',
      url: 'https://www.jiuzhousports.com',
    },
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(json) }} />;
}

export function FAQPageSchema() {
  const json = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '九州体育如何注册会员？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过九州体育官网或App，点击"会员注册"按钮，填写手机号码和验证码即可完成注册。注册即享高清赛事直播、专属竞猜赔率等多项特权。',
        },
      },
      {
        '@type': 'Question',
        name: '九州体育支持哪些赛事直播？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '九州体育覆盖200+全球顶级体育赛事，包括足球（英超、西甲、欧冠、中超等）、篮球（NBA、CBA等）、网球（四大满贯等）、电竞（LPL、LCK等）、F1、UFC、游泳世锦赛等热门赛事。',
        },
      },
      {
        '@type': 'Question',
        name: '九州体育APP如何下载？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以访问九州体育官网，点击"App下载"按钮，扫描二维码或根据提示选择iOS/Android版本下载。九州体育APP提供与网页版一致的全功能体验。',
        },
      },
      {
        '@type': 'Question',
        name: '九州体育竞猜的提款速度如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '九州体育采用先进的资金处理系统，提款申请审核通过后平均30秒即可到账，支持银行卡、微信、支付宝等多种提款方式，确保您的资金安全快速到账。',
        },
      },
      {
        '@type': 'Question',
        name: '九州体育的客服如何联系？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '九州体育提供7×24小时全天候客服支持。您可以通过官网在线客服、拨打客服热线400-888-9999或发送邮件至service@jiuzhousports.com联系我们。VIP会员还享有一对一专属客服服务。',
        },
      },
    ],
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(json) }} />;
}